// SPA entry point.
//
// During the migration, the heavy lifting still lives in index.html (the
// near-verbatim copy of the legacy monolith). This file bootstraps the auth
// check, exposes `api` on the window for the legacy inline JS to call, and
// — critically — monkey-patches window.fetch so legacy inline calls like
// `fetch('/api/...')` hit the backend on create.overdigital.ai instead of
// the static host at studio.overdigital.ai (which would return HTML 404s).

import { api, qrOfferApi } from './api.js';
import { ensureLoggedIn, logout, getCurrentUser } from './auth.js';

// ── fetch shim ────────────────────────────────────────────────────────────
// Any same-origin relative path that starts with /api, /share, /oauth2callback,
// or /__auth gets rewritten to hit the backend, with credentials and an
// Accept: application/json hint so the gateway returns JSON 401s instead of
// HTML login pages. We also force cache: 'no-store' — the gateway is stateful
// per request (auth session) so response bodies must never be cached.
const API_BASE = (import.meta.env.VITE_API_BASE || 'https://create.overdigital.ai').trim();
const BACKEND_PREFIXES = ['/api/', '/share/', '/oauth2callback', '/__auth/'];

const _origFetch = window.fetch.bind(window);
window.fetch = function patchedFetch(input, init = {}) {
  let url = (typeof input === 'string') ? input : (input && input.url);
  if (typeof url === 'string' && BACKEND_PREFIXES.some(p => url.startsWith(p))) {
    const rewritten = API_BASE + url;
    const mergedHeaders = new Headers(init.headers || (typeof input === 'object' && input.headers) || {});
    if (!mergedHeaders.has('Accept')) mergedHeaders.set('Accept', 'application/json');
    const patchedInit = {
      ...init,
      headers: mergedHeaders,
      credentials: init.credentials || 'include',
      cache: init.cache || 'no-store'
    };
    return _origFetch(rewritten, patchedInit);
  }
  return _origFetch(input, init);
};

// ── expose api/auth for any new code written alongside the legacy monolith ──
window.csApi = api;
window.csQrOfferApi = qrOfferApi;
window.csAuth = { ensureLoggedIn, logout, getCurrentUser };

// ── auth + UI sync ────────────────────────────────────────────────────────
// The legacy index.html calls its own `checkAuth()` at end-of-body which sets
// state.currentUser via showApp(). In production we've seen that path land on
// the 'user'/'User' fallback even when /api/me returns the correct data
// (likely a 304-from-stale-cache race between first-paint and shim install).
// Belt-and-suspenders: once ensureLoggedIn resolves with the authoritative
// user, force-sync the legacy state if it's out of sync.
ensureLoggedIn().then((me) => {
  if (!me || !me.user) return;
  try {
    const state = globalThis.state;
    const showApp = globalThis.showApp;
    const updateUserUI = globalThis.updateUserUI;
    if (state && state.currentUser !== me.user) {
      if (typeof showApp === 'function') {
        showApp(me.user, me.displayName, me.color);
      } else {
        state.currentUser = me.user;
        state.currentDisplayName = me.displayName || me.user;
        state.currentColor = me.color || '#C9A84C';
        if (typeof updateUserUI === 'function') updateUserUI();
      }
    }
  } catch { /* legacy globals may not be present if index.html was rewritten */ }
}).catch(() => { /* api.js already redirected on 401 */ });
